import React, { useMemo, useState } from 'react'
import { Box, Typography, IconButton } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import SwapVertIcon from '@mui/icons-material/SwapVert'
import { changeRoute } from '../../../store/slices/SessionSlice'
import { useAppDispatch } from '../../../store/Hooks'
import Helper from '../../../helpers/Helper'
import { IContract } from '../../../definitions/Contract'

interface CompareTableProps {
    fromContract: IContract
    toContract: IContract
    commonFields: string[]
    fieldData: {
        field: string
        label: string
        percentage: number
    }[]
}

const CompareTable: React.FC<CompareTableProps> = ({ fromContract, toContract, commonFields, fieldData }) => {
    const dispatch = useAppDispatch()
    const theme = useTheme()
    const [sortBy, setSortBy] = useState<'name' | 'percentage'>('name')

    const calculateColor = (isCommon: boolean): string => isCommon ? theme.palette.success.main : theme.palette.grey[400]
    const labelWidth = 100

    const sortedFields = useMemo(() => {
        return [...fieldData].sort((a, b) => {
            if (sortBy === 'name') {
                return a.label.localeCompare(b.label)
            }
            return b.percentage - a.percentage
        })
    }, [sortBy, fieldData])

    const nodesMapped = useMemo(() => {
        return sortedFields.map(fieldInfo => {
            const { field, label, percentage } = fieldInfo
            const fromValue = Helper.getNestedValue(fromContract, field)
            const toValue = Helper.getNestedValue(toContract, field)
            const isCommon = commonFields.includes(field)

            return {
                field,
                label,
                fromNode: {
                    value: fromValue !== undefined ? String(fromValue) : 'N/A'
                },
                toNode: {
                    value: toValue !== undefined ? String(toValue) : 'N/A'
                },
                isCommon,
                percentage
            }
        })
    }, [sortedFields, fromContract, toContract, commonFields])

    const handleNavigateToContract = (contract: IContract) => {
        dispatch(changeRoute({ route: 'contract-details', params: { contract, selectedTab: 'settings' } }))
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <IconButton
                    onClick={() => setSortBy(prev => (prev === 'name' ? 'percentage' : 'name'))}
                    title={`Order by ${sortBy === 'name' ? 'percentage' : 'name'}`}
                    sx={{
                        'border': '1px solid',
                        'borderColor': 'grey.500',
                        'borderRadius': '8px',
                        'padding': '8px',
                        'transition': 'all 0.3s ease',
                        '&:hover': {
                            backgroundColor: 'grey.700',
                            borderColor: 'white'
                        }
                    }}>
                    <SwapVertIcon />
                </IconButton>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'row', marginTop: 2, position: 'sticky', mb: 3 }}>
                <Box sx={{ width: labelWidth }} />
                <Box sx={{ flex: 1, textAlign: 'center', display: 'flex', alignItems: 'center', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ border: '1px solid #5936A2', borderRadius: 50, width: 120, backgroundColor: '#EDDDFE', padding: 0.5 }}>
                        <Typography sx={{ color: '#5936A2', fontWeight: 'bold' }}>Contract A</Typography>
                    </Box>
                    <Typography
                        sx={{
                            'cursor': 'pointer',
                            'textDecoration': 'none',
                            'transition': 'all 0.3s ease',
                            '&:hover': {
                                textDecoration: 'underline'
                            }
                        }}
                        onClick={() => handleNavigateToContract(fromContract)}
                        fontWeight='Bold'>
                        {fromContract.name}
                    </Typography>
                </Box>
                <Box sx={{ flex: 1 }} />
                <Box sx={{ flex: 1, textAlign: 'center', display: 'flex', alignItems: 'center', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ border: '1px solid #5936A2', borderRadius: 50, width: 120, backgroundColor: '#EDDDFE', padding: 0.5 }}>
                        <Typography sx={{ color: '#5936A2', fontWeight: 'bold' }}>Contract B</Typography>
                    </Box>
                    <Typography
                        sx={{
                            'cursor': 'pointer',
                            'textDecoration': 'none',
                            'transition': 'all 0.3s ease',
                            '&:hover': {
                                textDecoration: 'underline'
                            }
                        }}
                        onClick={() => handleNavigateToContract(toContract)}
                        fontWeight='Bold'>
                        {toContract.name}
                    </Typography>
                </Box>
            </Box>
            <Box sx={{ maxHeight: 900, overflow: 'auto' }}>
                {nodesMapped.map(node => (
                    <Box
                        key={node.field}
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            marginY: 1.5,
                            padding: 1
                        }}>
                        <Box sx={{ width: labelWidth }}>
                            <Typography variant='body2' sx={{ fontWeight: 'bold', fontSize: 11, paddingRight: 5, textAlign: 'end' }}>
                                {node.label}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                padding: 2,
                                borderRadius: 1,
                                border: '1px solid',
                                minHeight: 80,
                                maxHeight: 400,
                                minWidth: 180,
                                borderColor: theme.palette.text.primary,
                                textAlign: 'center',
                                justifyContent: 'center',
                                alignItems: 'center',
                                overflow: 'hidden'
                            }}>
                            <Typography
                                variant='body2'
                                sx={{
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    whiteSpaceCollapse: 'collapse'
                                }}>
                                {typeof node.fromNode.value === 'object'
                                    ? JSON.stringify(node.fromNode.value, null, 2)
                                    : node.fromNode.value}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative'
                            }}>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: 0,
                                    borderTop: '3px dashed',
                                    borderColor: theme.palette.grey[400],
                                    position: 'absolute'
                                }}
                            />
                            <Box
                                sx={{
                                    backgroundColor: calculateColor(node.isCommon),
                                    borderRadius: '50%',
                                    width: 40,
                                    height: 40,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 1
                                }}>
                                <Typography sx={{ color: 'black', fontSize: 11, fontWeight: 'Bold' }}>
                                    {node.percentage.toFixed(1)}%
                                </Typography>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                flex: 1,
                                padding: 1,
                                borderRadius: 1,
                                border: '1px solid',
                                minHeight: 60,
                                maxHeight: 250,
                                minWidth: 180,
                                borderColor: theme.palette.text.primary,
                                textAlign: 'center',
                                justifyContent: 'center',
                                display: 'flex',
                                alignItems: 'center',
                                overflow: 'hidden'
                            }}>
                            <Typography
                                variant='body2'
                                sx={{
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    whiteSpaceCollapse: 'collapse'
                                }}>
                                {typeof node.toNode.value === 'object'
                                    ? JSON.stringify(node.toNode.value, null, 2)
                                    : node.toNode.value}
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

export default CompareTable
